import { useEffect, useMemo, useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  BadgeCheck,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  CircleCheck,
  Copy,
  DollarSign,
  Link2,
  Network as NetworkIcon,
  Search,
  TrendingUp,
  Users,
} from "lucide-react";
import API from "../services/api";
import CustomSelect from "../components/CustomSelect";
import { getCurrentUser } from "../services/sessionCache";
import { formatAppDateTime } from "../utils/dateFormat";
import "./Network.css";

function toArray(value) {
  return Array.isArray(value) ? value : [];
}

function toObject(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

function pickFirst(...values) {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }
  return "";
}

function pickFirstNumber(...values) {
  for (const value of values) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
}

function normalizeBoolean(value) {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;

  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  if (["true", "1", "yes", "active"].includes(normalized)) return true;
  if (["false", "0", "no", "inactive"].includes(normalized)) return false;
  return false;
}

function formatShare(value) {
  return `${(Number(value) || 0).toFixed(2)}%`;
}

function formatCurrency(value) {
  const amount = Number(value);
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(amount) ? amount : 0);
}

function formatDateTime(value) {
  return formatAppDateTime(value);
}

function getReferralCode(user) {
  const referral = toObject(user?.referral);
  const invite = toObject(user?.invite);
  const network = toObject(user?.network);

  return String(
    pickFirst(
      user?.referralCode,
      user?.referral_code,
      user?.refCode,
      user?.ref_code,
      user?.inviteCode,
      user?.invite_code,
      referral.code,
      referral.referralCode,
      referral.refCode,
      invite.code,
      invite.inviteCode,
      network.referralCode,
      network.refCode,
    ) || "",
  );
}

function normalizeNetworkPayload(payload) {
  const root = toObject(payload);
  const data = toObject(root.data);
  const nested = toObject(
    pickFirst(
      data.network,
      data.referral,
      data.referrals,
      root.network,
      root.referral,
      root.referrals,
    ),
  );
  const source = Object.keys(nested).length ? nested : Object.keys(data).length ? data : root;
  const referrals = toArray(
    pickFirst(
      source.level1,
      source.directReferrals,
      source.directUsers,
      source.referrals,
      source.users,
    ),
  );

  return {
    referrals,
    directReferralCount:
      pickFirstNumber(
        source.directReferralCount,
        source.activeReferralCount,
        source.activeDirectReferralCount,
      ) ?? referrals.filter((referral) => normalizeBoolean(referral?.isActive)).length,
    commissionRate:
      pickFirstNumber(
        source.commissionRate,
        source.level1Share,
        source.currentCommissionRate,
        source.rate,
      ) ?? 0,
    tierLabel: String(
      pickFirst(source.tierLabel, source.currentTierLabel, source.currentTier) || "",
    ),
    totalReferralEarnings:
      pickFirstNumber(
        source.totalReferralEarnings,
        source.totalEarnings,
        source.referralEarnings,
        source.totalCommissionAmount,
      ) ?? 0,
    totalPaidReferralEarnings: pickFirstNumber(source.totalPaidReferralEarnings) ?? 0,
    totalUnpaidReferralEarnings: pickFirstNumber(source.totalUnpaidReferralEarnings) ?? 0,
    recentCommissions: toArray(
      pickFirst(
        source.recentCommissions,
        source.referralCommissions,
        source.commissions,
        source.ledger,
      ),
    ),
  };
}

const PAGE_SIZE_OPTIONS = [
  { value: "10", label: "10" },
  { value: "50", label: "50" },
  { value: "100", label: "100" },
];

export default function Network() {
  const [referrals, setReferrals] = useState([]);
  const [commissionRate, setCommissionRate] = useState(0);
  const [tierLabel, setTierLabel] = useState("");
  const [totalReferralEarnings, setTotalReferralEarnings] = useState(0);
  const [totalPaidReferralEarnings, setTotalPaidReferralEarnings] = useState(0);
  const [totalUnpaidReferralEarnings, setTotalUnpaidReferralEarnings] = useState(0);
  const [recentCommissions, setRecentCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copyStatus, setCopyStatus] = useState("");
  const [user, setUser] = useState(null);
  const [activitySearch, setActivitySearch] = useState("");
  const [activitySorting, setActivitySorting] = useState([]);
  const [activityPagination, setActivityPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const fetchNetworkData = async () => {
    setLoading(true);
    try {
      const res = await API.get("/users/network");
      const normalized = normalizeNetworkPayload(res?.data);

      setReferrals(normalized.referrals);
      setCommissionRate(normalized.commissionRate);
      setTierLabel(normalized.tierLabel);
      setTotalReferralEarnings(normalized.totalReferralEarnings);
      setTotalPaidReferralEarnings(normalized.totalPaidReferralEarnings);
      setTotalUnpaidReferralEarnings(normalized.totalUnpaidReferralEarnings);
      setRecentCommissions(normalized.recentCommissions);
    } catch (error) {
      console.error("Failed to load network data", error);
      setReferrals([]);
      setCommissionRate(0);
      setTierLabel("");
      setTotalReferralEarnings(0);
      setTotalPaidReferralEarnings(0);
      setTotalUnpaidReferralEarnings(0);
      setRecentCommissions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const bootstrapPage = async () => {
      try {
        const [userData] = await Promise.all([
          getCurrentUser().catch((error) => {
            console.error("Failed to load user data", error);
            return null;
          }),
          fetchNetworkData(),
        ]);

        if (!cancelled) {
          setUser(userData);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to bootstrap network page", error);
        }
      }
    };

    bootstrapPage();

    return () => {
      cancelled = true;
    };
  }, []);

  const referralCode = getReferralCode(user);
  const referralLink = referralCode ? `${window.location.origin}/signup?ref=${referralCode}` : "";
  const canCopyReferralLink = Boolean(referralLink);

  const copyReferralLink = async () => {
    if (!canCopyReferralLink) return;

    try {
      await navigator.clipboard.writeText(referralLink);
      setCopyStatus("copied");
    } catch (error) {
      console.error("Copy failed", error);
      setCopyStatus("error");
    } finally {
      window.setTimeout(() => setCopyStatus(""), 2000);
    }
  };

  const activitySkeletonRows = Array.from({ length: 6 });

  const activityRows = useMemo(() => {
    const latestCommissionByReferralId = new Map();
    const latestCommissionByReferralEmail = new Map();

    recentCommissions.forEach((commission, index) => {
      const referredUser = toObject(commission?.referredUser);
      const referredUserId = pickFirst(referredUser.id, referredUser._id);
      const referredUserEmail = String(pickFirst(referredUser.email) || "")
        .trim()
        .toLowerCase();
      const dateValue = commission?.createdAt || commission?.updatedAt || "";
      const nextCommission = {
        id: `commission-${pickFirst(commission?.id, commission?._id, commission?.sourceRef, index)}`,
        planName: String(pickFirst(commission?.planName, commission?.plan?.name) || "—"),
        planAmount: pickFirstNumber(commission?.subscriptionAmount, commission?.planAmount),
        rateValue: pickFirstNumber(commission?.commissionRate, commission?.rate),
        commissionAmount: pickFirstNumber(commission?.commissionAmount, commission?.amountEarned),
        sourceType: String(pickFirst(commission?.sourceType, commission?.source) || "—"),
        payoutStatus: String(commission?.payoutStatus || "unpaid"),
        paidAt: commission?.paidAt || null,
        payoutNote: commission?.payoutNote || null,
        payoutReference: commission?.payoutReference || null,
        dateValue,
      };
      const nextTime = dateValue ? new Date(dateValue).getTime() : Number.NEGATIVE_INFINITY;

      if (referredUserId) {
        const existing = latestCommissionByReferralId.get(String(referredUserId));
        const existingTime = existing?.dateValue
          ? new Date(existing.dateValue).getTime()
          : Number.NEGATIVE_INFINITY;
        if (!existing || nextTime >= existingTime) {
          latestCommissionByReferralId.set(String(referredUserId), nextCommission);
        }
      }

      if (referredUserEmail) {
        const existing = latestCommissionByReferralEmail.get(referredUserEmail);
        const existingTime = existing?.dateValue
          ? new Date(existing.dateValue).getTime()
          : Number.NEGATIVE_INFINITY;
        if (!existing || nextTime >= existingTime) {
          latestCommissionByReferralEmail.set(referredUserEmail, nextCommission);
        }
      }
    });

    return referrals.map((referral, index) => {
      const displayName = String(
        pickFirst(referral.displayName, referral.name, referral.username, referral.email) ||
          "Unnamed referral",
      );
      const email = String(pickFirst(referral.email) || "");
      const emailKey = email.trim().toLowerCase();
      const referralId = pickFirst(referral.id, referral._id);
      const isActive = normalizeBoolean(referral?.isActive);
      const mt4Linked = normalizeBoolean(referral?.mt4Linked);
      const mt5Linked = normalizeBoolean(referral?.mt5Linked);
      const linkedCommission =
        (referralId && latestCommissionByReferralId.get(String(referralId))) ||
        (emailKey && latestCommissionByReferralEmail.get(emailKey)) ||
        null;

      return {
        id: `referral-${pickFirst(referral.id, referral._id, referral.email, index)}`,
        displayName,
        email,
        statusItems: [
          {
            label: isActive ? "Active" : "Inactive",
            className: `network-status-badge network-status-badge-${isActive ? "active" : "inactive"}`,
          },
          {
            label: "MT4",
            className: `network-status-badge ${mt4Linked ? "network-status-badge-mt4" : "network-status-badge-muted"}`,
          },
          {
            label: "MT5",
            className: `network-status-badge ${mt5Linked ? "network-status-badge-mt5" : "network-status-badge-muted"}`,
          },
        ],
        planName: linkedCommission?.planName || "—",
        planAmount: linkedCommission?.planAmount ?? null,
        rateValue: linkedCommission?.rateValue ?? null,
        commissionAmount: linkedCommission?.commissionAmount ?? null,
        payoutStatus: linkedCommission?.payoutStatus ?? null,
        paidAt: linkedCommission?.paidAt ?? null,
        payoutNote: linkedCommission?.payoutNote ?? null,
        payoutReference: linkedCommission?.payoutReference ?? null,
        dateValue: linkedCommission?.dateValue || "",
        searchText: [
          displayName,
          email,
          isActive ? "active" : "inactive",
          mt4Linked ? "mt4 linked" : "mt4 not linked",
          mt5Linked ? "mt5 linked" : "mt5 not linked",
          linkedCommission?.planName,
          linkedCommission?.sourceType,
          linkedCommission?.planAmount,
          linkedCommission?.rateValue,
          linkedCommission?.commissionAmount,
          linkedCommission?.dateValue,
        ]
          .map((value) => String(value || "").toLowerCase())
          .join(" "),
      };
    });
  }, [recentCommissions, referrals]);

  const filteredActivityRows = useMemo(() => {
    const searchTerm = activitySearch.trim().toLowerCase();
    return activityRows.filter((row) => !searchTerm || row.searchText.includes(searchTerm));
  }, [activityRows, activitySearch]);

  useEffect(() => {
    setActivityPagination((current) => ({
      ...current,
      pageIndex: 0,
    }));
  }, [activitySearch]);

  const activityColumns = useMemo(
    () => [
      {
        accessorKey: "displayName",
        header: "Referral",
        cell: ({ row }) => (
          <div className="network-referral-copy">
            <strong>{row.original.displayName}</strong>
            {row.original.email ? (
              <div className="network-referral-meta">
                <span>{row.original.email}</span>
              </div>
            ) : null}
          </div>
        ),
      },
      {
        accessorKey: "statusItems",
        header: "Status",
        cell: ({ row }) => {
          const [activeItem, mt4Item, mt5Item] = row.original.statusItems;
          return (
            <div className="network-status-group">
              <span className={activeItem.className}>{activeItem.label}</span>
              <div className="network-status-terminals">
                <span className={mt4Item.className}>{mt4Item.label}</span>
                <span className={mt5Item.className}>{mt5Item.label}</span>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: "planName",
        header: "Plan",
        cell: ({ row }) => (
          <div className="network-referral-copy">
            <strong>{row.original.planName}</strong>
            {row.original.planAmount !== null ? (
              <div className="network-referral-meta">
                <span>{formatCurrency(row.original.planAmount)}</span>
              </div>
            ) : null}
          </div>
        ),
      },
      {
        accessorKey: "rateValue",
        header: "Rate",
        cell: ({ row }) =>
          row.original.rateValue === null ? "—" : formatShare(row.original.rateValue),
      },
      {
        accessorKey: "commissionAmount",
        header: "Commission",
        cell: ({ row }) =>
          row.original.commissionAmount === null
            ? "—"
            : formatCurrency(row.original.commissionAmount),
      },
      {
        accessorKey: "payoutStatus",
        header: "Payout",
        cell: ({ row }) => {
          if (row.original.payoutStatus === null) return "—";
          const isPaid = row.original.payoutStatus === "paid";
          return (
            <div className="network-payout-status-cell">
              <span
                className={`network-status-badge ${isPaid ? "network-status-badge-paid" : "network-status-badge-unpaid"}`}
              >
                {isPaid ? "Paid" : "Unpaid"}
              </span>
              {isPaid && row.original.paidAt ? (
                <div className="network-referral-meta">
                  <span>{formatDateTime(row.original.paidAt)}</span>
                </div>
              ) : null}
            </div>
          );
        },
      },
      {
        accessorKey: "payoutNote",
        header: "Note / Ref",
        cell: ({ row }) => {
          const { payoutNote, payoutReference } = row.original;
          if (!payoutNote && !payoutReference)
            return <span className="network-payout-empty">—</span>;
          return (
            <div className="network-payout-note-cell">
              {payoutNote ? <span className="network-payout-note">{payoutNote}</span> : null}
              {payoutReference ? (
                <span className="network-payout-ref">{payoutReference}</span>
              ) : null}
            </div>
          );
        },
      },
      {
        accessorKey: "dateValue",
        header: "Date",
        cell: ({ row }) => (row.original.dateValue ? formatDateTime(row.original.dateValue) : "—"),
      },
    ],
    [],
  );

  const activityTable = useReactTable({
    data: filteredActivityRows,
    columns: activityColumns,
    state: {
      sorting: activitySorting,
      pagination: activityPagination,
    },
    onSortingChange: setActivitySorting,
    onPaginationChange: setActivityPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    sortDescFirst: true,
  });

  const activityRowsPage = activityTable.getRowModel().rows;
  const activityRowStart =
    filteredActivityRows.length === 0
      ? 0
      : activityPagination.pageIndex * activityPagination.pageSize + 1;
  const activityRowEnd = Math.min(
    (activityPagination.pageIndex + 1) * activityPagination.pageSize,
    filteredActivityRows.length,
  );

  const renderHeaderSort = (header) => {
    const sorted = header.column.getIsSorted();

    return (
      <button
        type="button"
        className={`network-table-sort${sorted ? " network-table-sort-active" : ""}`}
        onClick={header.column.getToggleSortingHandler()}
      >
        <span>{flexRender(header.column.columnDef.header, header.getContext())}</span>
        {sorted ? (
          <ChevronDown
            className={`network-table-sort-icon${sorted === "desc" ? " network-table-sort-icon-desc" : ""}`}
          />
        ) : (
          <ChevronsUpDown className="network-table-sort-icon" />
        )}
      </button>
    );
  };

  return (
    <div className="network-page">
      <div className="network-header">
        <div className="network-header-copy">
          <h1>
            <NetworkIcon className="network-header-icon" />
            <span>My Network</span>
          </h1>
          <p>
            Share your referral link, invite more people, and keep track of the rewards you earn.
          </p>
        </div>
      </div>

      <div className="network-summary-grid">
        <div className="network-summary-card">
          <div className="network-summary-icon">
            <Users size={20} />
          </div>
          <div className="network-summary-copy">
            <span>Total Referrals</span>
            <strong>{referrals.length}</strong>
            <small>Joined through your link</small>
          </div>
        </div>

        <div className="network-summary-card">
          <div className="network-summary-icon">
            <TrendingUp size={20} />
          </div>
          <div className="network-summary-copy">
            <span>Current Rate</span>
            <strong>{formatShare(commissionRate)}</strong>
            <small>{tierLabel || "Your current referral tier"}</small>
          </div>
        </div>

        <div className="network-summary-card">
          <div className="network-summary-icon">
            <DollarSign size={20} />
          </div>
          <div className="network-summary-copy">
            <span>Total Earnings</span>
            <strong>{formatCurrency(totalReferralEarnings)}</strong>
            <small>Total referral rewards</small>
          </div>
        </div>

        <div className="network-summary-card">
          <div className="network-summary-icon network-summary-icon-paid">
            <BadgeCheck size={20} />
          </div>
          <div className="network-summary-copy">
            <span>Paid / Pending</span>
            <strong>{formatCurrency(totalPaidReferralEarnings)}</strong>
            <small>Pending: {formatCurrency(totalUnpaidReferralEarnings)}</small>
          </div>
        </div>
      </div>

      <div className="network-stack">
        <section className="network-card">
          <div className="network-card-heading">
            <div className="network-card-copy">
              <div className="network-card-title">
                <Link2 />
                <span>Affiliate Access</span>
              </div>
              <p>Your personal referral link and code are ready to share with new users.</p>
            </div>
          </div>

          <div className="network-link-settings-row">
            <div className="network-field-group network-referral-link-field">
              <label className="network-label">Referral Link</label>
              <input
                type="text"
                value={referralLink}
                readOnly
                onFocus={(event) => event.target.select()}
                className="network-input network-input-readonly"
                placeholder="Referral link will appear here"
              />
            </div>

            <div className="network-field-group network-referral-code-field">
              <label className="network-label">Referral Code</label>
              <input
                type="text"
                value={referralCode}
                readOnly
                onFocus={(event) => event.target.select()}
                className="network-input network-input-readonly"
                placeholder="Referral code will appear here"
              />
            </div>

            <div className="network-link-actions">
              <button
                type="button"
                onClick={copyReferralLink}
                className="network-btn network-btn-success"
                disabled={!canCopyReferralLink}
              >
                {copyStatus === "copied" ? (
                  <>
                    <CircleCheck size={16} />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy size={16} />
                    Copy Link
                  </>
                )}
              </button>
            </div>
          </div>

          <p className="network-access-note">
            {copyStatus === "copied"
              ? "Your referral link is copied and ready to share."
              : "When one of your referrals becomes active, your reward is added automatically."}
          </p>
        </section>

        <section className="network-card">
          <div className="network-card-heading network-card-heading-badged">
            <div className="network-card-copy">
              <div className="network-card-title">
                <Users />
                <span>Affiliate Activity</span>
              </div>
              <p>See your referrals and reward activity together in one place.</p>
            </div>
            <span className="network-pill network-pill-total">{filteredActivityRows.length}</span>
          </div>

          <div className="network-toolbar network-toolbar-compact">
            <div className="network-field-group network-toolbar-search">
              <label className="network-label">
                <Search size={13} />
                Search Activity
              </label>
              <input
                type="text"
                className="network-input"
                placeholder="Search by name, email, plan, or source"
                value={activitySearch}
                onChange={(event) => setActivitySearch(event.target.value)}
              />
            </div>

            <div className="network-field-group">
              <label className="network-label">Count</label>
              <CustomSelect
                value={String(activityPagination.pageSize)}
                onChange={(value) => activityTable.setPageSize(Number(value))}
                options={PAGE_SIZE_OPTIONS}
              />
            </div>
          </div>

          <div className="network-data-table-wrap">
            <table className="network-data-table network-data-table-activity">
              <thead>
                <tr>
                  <th>S. No.</th>
                  {activityTable.getHeaderGroups()[0].headers.map((header) => (
                    <th key={header.id}>
                      {header.column.getCanSort()
                        ? renderHeaderSort(header)
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  activitySkeletonRows.map((_, index) => (
                    <tr
                      key={`network-activity-skeleton-${index}`}
                      className="network-table-skeleton-row"
                    >
                      <td>
                        <span className="network-skeleton-line network-skeleton-line-xs" />
                      </td>
                      <td>
                        <span className="network-skeleton-line network-skeleton-line-lg" />
                      </td>
                      <td>
                        <span className="network-skeleton-line network-skeleton-line-md" />
                      </td>
                      <td>
                        <span className="network-skeleton-line network-skeleton-line-md" />
                      </td>
                      <td>
                        <span className="network-skeleton-line network-skeleton-line-sm" />
                      </td>
                      <td>
                        <span className="network-skeleton-line network-skeleton-line-sm" />
                      </td>
                      <td>
                        <span className="network-skeleton-line network-skeleton-line-md" />
                      </td>
                      <td>
                        <span className="network-skeleton-line network-skeleton-line-md" />
                      </td>
                      <td>
                        <span className="network-skeleton-line network-skeleton-line-sm" />
                      </td>
                    </tr>
                  ))
                ) : filteredActivityRows.length > 0 ? (
                  activityRowsPage.map((row, rowIndex) => (
                    <tr key={row.id}>
                      <td>
                        {activityPagination.pageIndex * activityPagination.pageSize + rowIndex + 1}
                      </td>
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr className="network-empty-table-row">
                    <td
                      colSpan={activityTable.getVisibleLeafColumns().length + 1}
                      className="network-empty-table-cell"
                    >
                      <div className="network-empty-table-state">
                        {activityRows.length > 0
                          ? "No results match your search."
                          : "No activity yet. Share your referral link to get started."}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {!loading && filteredActivityRows.length > 0 ? (
            <div className="network-table-footer">
              <span className="network-table-summary">
                Showing {activityRowStart}-{activityRowEnd} of {filteredActivityRows.length}
              </span>

              <div className="network-table-pagination">
                <button
                  type="button"
                  className="network-btn network-btn-ghost"
                  onClick={() => activityTable.previousPage()}
                  disabled={!activityTable.getCanPreviousPage()}
                >
                  <ChevronLeft size={15} />
                  Prev
                </button>
                <span className="network-pagecount">
                  Page {activityPagination.pageIndex + 1} / {activityTable.getPageCount() || 1}
                </span>
                <button
                  type="button"
                  className="network-btn network-btn-ghost"
                  onClick={() => activityTable.nextPage()}
                  disabled={!activityTable.getCanNextPage()}
                >
                  Next
                  <ChevronRight size={15} />
                </button>
              </div>
            </div>
          ) : null}
        </section>
      </div>
    </div>
  );
}
